/* Stale-while-revalidate for the store.
 *
 * A thunk hands its screen the last response it saw for a request straight
 * away, then asks the network and dispatches again only if something changed.
 *
 * This is the half the service worker cannot do. The worker stays network-first
 * for API GETs (see sw.ts) because the app reads back what it just wrote, and a
 * refetch answered from its cache returned the pre-mutation rows. Here the
 * network is always asked and always has the last word; the stored copy is only
 * ever what the screen shows while it waits.
 *
 * Keyed per request, not per slice: several endpoints write one slice with
 * different rows (usersReducer.users is filled by /users, by the managers list
 * and by users + managers), so a slice-level copy would show one screen's rows
 * on another. The per-screen clean* on unmount stays for the same reason.
 *
 * Kept in memory and mirrored to CacheStorage, so a cold start -- every launch
 * of the installed app -- has something to show. The worker's activate handler
 * deletes every cache but its own two, so a deploy drops this one and an old
 * response shape never reaches new code.
 */

// `api-` prefixed, so clearApiCache() sweeps it along with the worker's copy.
const CACHE_NAME = 'api-store';
const PATH_PREFIX = '/__store/';

const memory = new Map<string, string>();

// Bumped on login and logout. A response requested under one session must not
// be stored or shown under the next.
let session = 0;

/* The API methods are typed Promise<AxiosResponse<T>> but resolve to T: the
 * response interceptor returns `data`, and src/@types/axios.d.ts makes
 * AxiosResponse<T> a Promise<T> so that `await` agrees. Generic inference does
 * not peel that second layer, so it is peeled here. */
type Unwrapped<R> = R extends PromiseLike<infer U> ? U : R;

const toPath = (key: string) => `${PATH_PREFIX}${encodeURIComponent(key)}`;

const toKey = (url: string) => decodeURIComponent(new URL(url).pathname.slice(PATH_PREFIX.length));

const persist = (key: string, json: string) => {
  if (typeof caches === 'undefined') return;

  const started = session;

  caches.open(CACHE_NAME)
    .then((cache) => {
      if (started !== session) return undefined;

      return cache.put(toPath(key), new Response(json, {
        headers: { 'Content-Type': 'application/json' },
      }));
    })
    .catch(() => undefined);
};

/**
 * Loads the stored responses into memory. Awaited once before the app mounts,
 * so every later read is synchronous.
 */
export const hydrateResponses = async (): Promise<void> => {
  if (typeof caches === 'undefined') return;

  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();

    await Promise.all(requests.map(async (request) => {
      const response = await cache.match(request);
      if (!response) return;

      const key = toKey(request.url);
      const json = await response.text();

      // The app mounts without waiting if this is slow, and a fresh response
      // that beat it here must not be overwritten by the older copy.
      if (!memory.has(key)) memory.set(key, json);
    }));
  } catch {
    // Storage can be unavailable (private mode, blocked site data) -- start cold.
  }
};

/** Drops every stored response. Runs on login and logout. */
export const forgetResponses = async (): Promise<void> => {
  session += 1;
  memory.clear();

  if (typeof caches === 'undefined') return;

  try {
    await caches.delete(CACHE_NAME);
  } catch {
    // See hydrateResponses.
  }
};

// The newest load per slot, see RevalidateOptions.
const newest = new Map<string, number>();
let loads = 0;

interface RevalidateOptions {
  /* The slice, or slices, `apply` writes. Required, because the newest load per
   * slot wins and an older one landing later is stored but not applied. Without
   * that, a screen left mid-load overwrites the screen that replaced it -- Me's
   * users + managers landing on the Users list -- and since an unchanged
   * response is not re-applied, the wrong rows stay. One thunk called with two
   * ids is the same race: user 5's late response must not land under user 7. */
  slot: string | string[];
  /* Wait for the network instead of showing the stored copy first. For edit
   * forms: a form filled from the stored copy would be refilled when a changed
   * fresh copy lands, over whatever the user has typed by then. */
  skipStored?: boolean;
}

/**
 * Hands `apply` the stored response for `key`, if there is one, then fetches
 * and hands over the fresh one only when it differs.
 *
 * The stored copy is applied before the first await, so a thunk dispatched from
 * a layout effect renders it before the browser paints and no Loader shows.
 * `apply` gets its own object every time and may mutate it -- several thunks
 * sort or reverse in place.
 *
 * A key names one response shape: two thunks that load the same thing share a
 * key and may apply it differently.
 */
export const revalidate = async <R>(
  key: string,
  load: () => Promise<R>,
  apply: (data: Unwrapped<R>) => void,
  { slot, skipStored = false }: RevalidateOptions,
): Promise<void> => {
  const started = session;
  const stored = skipStored ? undefined : memory.get(key);
  const slots = typeof slot === 'string' ? [slot] : slot;

  loads += 1;
  const id = loads;
  slots.forEach((name) => newest.set(name, id));

  if (stored !== undefined) apply(JSON.parse(stored));

  const fresh = (await load()) as unknown as Unwrapped<R>;

  if (started !== session) return;

  const json = JSON.stringify(fresh);

  if (json !== memory.get(key)) {
    memory.set(key, json);
    persist(key, json);
  }

  // The copy on screen is already current.
  if (json === stored) return;

  if (slots.some((name) => newest.get(name) !== id)) return;

  apply(fresh);
};
